import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";

type Flag = "leaderboard_visible" | "leaderboard_show_individuals" | "scores_visible";
function fmt(n: number) { const v = Number(n); return Number.isInteger(v) ? String(v) : v.toFixed(1); }

export function TopShopLeaderboardTab({ programId }: { programId: string }) {
  const qc = useQueryClient();
  const flagsKey = ["admin", "top-shop", "visibility", programId];
  const { data: flags } = useQuery({
    queryKey: flagsKey,
    queryFn: async () => {
      const { data, error } = await supabase.from("programs")
        .select("leaderboard_visible, leaderboard_show_individuals, scores_visible").eq("id", programId).single();
      if (error) throw error;
      return data;
    },
  });
  const { data: board, isLoading, error } = useQuery({
    queryKey: ["admin", "top-shop", "leaderboard"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_top_shop_leaderboard" as any);
      if (error) throw error;
      return data as any;
    },
  });

  const toggle = async (flag: Flag, value: boolean) => {
    const { error } = await supabase.from("programs").update({ [flag]: value } as any).eq("id", programId);
    if (error) { toast.error(error.message); return; }
    toast.success(value ? "Turned on" : "Turned off");
    qc.invalidateQueries({ queryKey: flagsKey });
    qc.invalidateQueries({ queryKey: ["top-shop"] });
  };

  const switches: { flag: Flag; label: string; help: string; disabled?: boolean }[] = [
    { flag: "leaderboard_visible", label: "Show leaderboard to participants", help: "When off, only admins see standings." },
    { flag: "leaderboard_show_individuals", label: "Include individual shops on the participant leaderboard", help: "When off, participants see community standings only. Only shops that agreed to share their details are listed.", disabled: !flags?.leaderboard_visible },
    { flag: "scores_visible", label: "Show participants their own scores", help: "Participants see their per-challenge scores in their Top Shop area." },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-lg">Participant visibility</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {!flags ? <Loader2 className="h-5 w-5 animate-spin" /> : switches.map((s) => (
            <div key={s.flag} className="flex items-start justify-between gap-4">
              <div>
                <Label htmlFor={`sw-${s.flag}`} className="font-medium">{s.label}</Label>
                <p className="text-sm text-muted-foreground">{s.help}</p>
              </div>
              <Switch id={`sw-${s.flag}`} checked={!!flags[s.flag]} disabled={s.disabled} onCheckedChange={(v) => toggle(s.flag, v)} />
            </div>
          ))}
        </CardContent>
      </Card>

      {isLoading ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div> : error ? (
        <Card><CardContent className="p-6 text-sm text-destructive">Could not load the leaderboard.</CardContent></Card>
      ) : (
        <>
          <Card>
            <CardHeader><CardTitle className="text-lg">Community standings</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto p-0">
              <Table>
                <TableHeader><TableRow><TableHead>Rank</TableHead><TableHead>Community</TableHead><TableHead>Parish</TableHead><TableHead className="text-right">Participants</TableHead><TableHead className="text-right">Average</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(board?.communities ?? []).length === 0 && <TableRow><TableCell colSpan={6} className="py-6 text-center text-muted-foreground">No accepted participants yet.</TableCell></TableRow>}
                  {(board?.communities ?? []).map((r: any) => (
                    <TableRow key={r.community}><TableCell>{r.rank}</TableCell><TableCell>{r.community}</TableCell><TableCell>{r.parish ?? ""}</TableCell>
                      <TableCell className="text-right">{r.participants}</TableCell><TableCell className="text-right tabular-nums">{Number(r.average_score).toFixed(1)}</TableCell><TableCell className="text-right tabular-nums">{fmt(r.total_score)}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-lg">Individual standings</CardTitle><CardDescription>Admins see every accepted participant.</CardDescription></CardHeader>
            <CardContent className="overflow-x-auto p-0">
              <Table>
                <TableHeader><TableRow><TableHead>Rank</TableHead><TableHead>Name</TableHead><TableHead>Shop</TableHead><TableHead>Community</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(board?.individuals ?? []).map((r: any, i: number) => (
                    <TableRow key={i}><TableCell>{r.rank}</TableCell><TableCell>{r.full_name ?? ""}</TableCell><TableCell>{r.business_name}</TableCell>
                      <TableCell>{r.community ?? ""}</TableCell><TableCell className="text-right tabular-nums">{fmt(r.total_score)}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
